import React from "react"
import { DateTime, Interval } from "luxon"
import { BIG_CALENDAR_VIEWS } from "../consts/bigCalendar.consts"
import {
  EVENT_TYPES,
  HOLIDAY_SCHEMAS,
  VACATION_STATES,
} from "../consts/modules/vacations.consts"
import MexicanFlagIcon from "../design/assests/images/icons/mexican-flag.png"
import SISUIcon from "../design/assests/images/icons/sisu-icon.png"
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined"
import { compareDates, getDateDDMMYYY, getDateFormatDDMMYYYFromJSDate } from "./date.utils"
import {
  isEventPeriodDelimiter,
} from "./vacation.utils"
import { clsx } from "./strings.utils"
import PaidIcon from "@mui/icons-material/Paid"
import ThumbUpIcon from "@mui/icons-material/ThumbUp"
import ClockIcon from "@mui/icons-material/AccessTimeFilled"
import ChangeCircle from "@mui/icons-material/ChangeCircle"
import { ReactComponent as BlockedIcon } from "../design/assests/images/icons/material_block_filled_icon.svg"
import ThumbDownIcon from "@mui/icons-material/ThumbDown"

/** @typedef {import("../services/vacation.service.types").getTeamVacationsRequestsByYearResponse} VacationRequest */
export function splitEventOnWeekend(event) {
  const start = DateTime.fromJSDate(event.start)
  const end = DateTime.fromJSDate(event.end)

  const result = []
  let cursor = start

  while (cursor <= end) {
    if (cursor.weekday === 6 || cursor.weekday === 7) {
      const daysToNextMonday = (8 - cursor.weekday) % 7
      cursor = cursor.plus({ days: daysToNextMonday }).startOf("day")
      if (cursor > end) {
        break
      }
    }

    const daysToFriday = 5 - cursor.weekday
    const weekFriday = cursor.plus({ days: daysToFriday }).endOf("day")
    const sliceEnd = weekFriday < end ? weekFriday : end

    result.push({
      ...event,
      start: cursor.toJSDate(),
      end: sliceEnd.toJSDate(),
    })

    cursor = sliceEnd.plus({ milliseconds: 1 })
  }

  return result
}

export const splitEventSkippingWeekends = (events) => {
  const splitEvents = []

  events.forEach((event, index) => {
    let start = DateTime.fromJSDate(event.start)
    const end = DateTime.fromJSDate(event.end)
    let isSplitByWeekend = false
    if (isEventPeriodDelimiter(event)) {
      splitEvents.push({
        ...event,
        start: start.toJSDate(),
        end: end.toJSDate(),
      })
      return
    }
    while (start < end) {
      while (start.weekday === 6 || start.weekday === 7) {
        start = start.plus({ days: 1 })
        isSplitByWeekend = true
      }

      let rangeEnd = start
      while (
        rangeEnd < end &&
        rangeEnd.weekday !== 6 &&
        rangeEnd.weekday !== 7
      ) {
        rangeEnd = rangeEnd.plus({ days: 1 })
      }

      splitEvents.push({
        ...event,
        start: start.toJSDate(),
        end: rangeEnd.toJSDate(),
        isSplitByWeekend,
      })

      start = rangeEnd
    }
  })
  return splitEvents.map((event, index) => ({
    ...event,
    id: index,
  }))
}

export const defaultDayPropGetter = (date, hoveredDate) => {
  const day = date.getDay()
  if (day === 0 || day === 6) {
    return {
      className: "rbc-weekend",
    }
  }
  const dateClass = date.toISOString().split("T")[0]
  if (hoveredDate && compareDates(date, hoveredDate)) {
    return {
      className: `${dateClass} hovered-cell`,
    }
  }
  return {
    className: dateClass,
  }
}

export const defaultEventPropGetter = (event, currentDate, currentView) => {
  const eventStart = DateTime.fromJSDate(event.start)
  const visibleMonth = DateTime.fromJSDate(currentDate).month
  const visibleYear = DateTime.fromJSDate(currentDate).year
  if (
    (currentView === BIG_CALENDAR_VIEWS.MONTH &&
      eventStart.month !== visibleMonth) ||
    eventStart.year !== visibleYear
  ) {
    return {
      style: {
        opacity: 0.5,
      },
    }
  }

  return {}
}

/**
 * Adjusts each period key to the earliest event start and latest event end
 * in that period. E.g. if a period is "2025-07-17 - 2025-07-18" but all
 * events start on the 18th, the key becomes "2025-07-18 - 2025-07-18".
 *
 * Events may have `start`/`end` (JSDate) or `startDate`/`endDate` (ISO).
 *
 * @param {Record<string, Array<{start?: Date, end?: Date, startDate?: string, endDate?: string, eventType?: string}>>} eventsInTimeSpanSplitByWeekend
 * @returns {Record<string, Array>} New object with adjusted period keys
 */
export function adjustPeriodKeysToEventBounds(eventsInTimeSpanSplitByWeekend) {
  const result = {}
  for (const [periodKey, events] of Object.entries(eventsInTimeSpanSplitByWeekend)) {
    if (!events || events.length === 0) {
      result[periodKey] = events
      continue
    }

    let earliest = null
    let latest = null

    for (const event of events) {
      let startDt = null;
      let endDt = null;

      if (event.eventType === EVENT_TYPES.HOLIDAY) {
        startDt = DateTime.fromJSDate(event.start).toUTC().startOf("day");
        endDt = DateTime.fromJSDate(event.end).toUTC().startOf("day");
      } else {
        startDt =
          event.start != null
            ? DateTime.fromJSDate(event.start).toUTC().startOf("day")
            : event.startDate
              ? DateTime.fromISO(event.startDate).toUTC().startOf("day")
              : null
        endDt =
          event.end != null
            ? DateTime.fromJSDate(event.end).toUTC().startOf("day")
            : event.endDate
              ? DateTime.fromISO(event.endDate).toUTC().startOf("day")
              : null
      }


      if (startDt !== null && (earliest === null || startDt < earliest)) {
        earliest = startDt
      }
      if (endDt !== null && (latest === null || endDt > latest)) {
        latest = endDt
      }
    }

    const newKey =
      earliest != null && latest != null
        ? `${earliest.toString()} - ${latest.toString()}`
        : periodKey

    result[newKey] = events
  }
  return result
}

/**
 * @typedef {Object} RawVacationEvent
 * @property {string} eventType - Type of event ('vacation' | 'holiday')
 * @property {Date} start - Event start date as JS Date
 * @property {Date} end - Event end date as JS Date
 * @property {string} [startDate] - ISO string start date (for vacations)
 * @property {string} [endDate] - ISO string end date (for vacations)
 * @property {string|number} [vacationId] - Unique vacation identifier
 * @property {string} [status] - Vacation status (pending, approved, etc.)
 * @property {string} [title] - Event title/name
 */

/**
 * @typedef {Object} NormalizedEvent
 * @property {import('luxon').DateTime} _startDt - Normalized UTC start DateTime
 * @property {import('luxon').DateTime} _endDt - Normalized UTC end DateTime
 * @property {number} _durationDays - Duration in calendar days
 * @property {boolean} [_isSplit] - Whether this event was split from a multi-week event
 * @property {import('luxon').DateTime} [_originalStartDt] - Original start before split
 * @property {import('luxon').DateTime} [_originalEndDt] - Original end before split
 * @property {boolean} [_isGrouped] - Whether this is a grouped event container
 * @property {NormalizedEvent[]} [groupedEvents] - Child events if this is a group
 */

/**
 * @typedef {Object.<string, number>} PriorityMap
 * Status-to-priority mapping where lower numbers indicate higher display priority.
 */

/**
 * Priority mapping for "Pendientes" (Pending) filter view.
 * @type {PriorityMap}
 */
const PENDING_PRIORITY = {
  [VACATION_STATES.PENDING]: 1,
  [VACATION_STATES.CANCEL_REQUEST]: 2,
  [VACATION_STATES.CHANGE_REQUEST]: 3,
  [VACATION_STATES.APPROVED_AND_PENDING_PLUS_DAYS_PAY_DATE]: 4,
};

/**
 * Priority mapping for "Por Tomar" (Upcoming) filter view.
 * @type {PriorityMap}
 */
const UPCOMING_PRIORITY = {
  [VACATION_STATES.APPROVED]: 1,
  [VACATION_STATES.APPROVED_AND_PENDING_PLUS_DAYS_PAY_DATE]: 2,
};

/**
 * Maximum events displayed in a single calendar cell before grouping is triggered.
 * @type {number}
 */
const MAX_EVENTS_PER_CELL = 4;

/**
 * Normalizes a raw vacation/holiday event by adding UTC-normalized Luxon DateTime objects.
 * Handles both ISO string dates and JavaScript Date objects as input.
 * 
 * @param {RawVacationEvent} event - Raw event from the API
 * @returns {NormalizedEvent} Event augmented with `_startDt`, `_endDt`, and `_durationDays`
 */
function normalizeEventDates(event) {
  const isHoliday = event.eventType === EVENT_TYPES.HOLIDAY;
  
  let startDt;
  let endDt;
  
  if (isHoliday) {
    startDt = DateTime.fromJSDate(event.start).toUTC().startOf("day");
    endDt = startDt;
    
    // Set to local end-of-day (23:59) so holidays render at bottom of calendar cell
    const startLocal = DateTime.fromJSDate(event.start).startOf("day");
    const endOfDayLocal = startLocal.endOf("day");
    
    return {
      ...event,
      start: endOfDayLocal.toJSDate(),
      end: endOfDayLocal.toJSDate(),
      _startDt: startDt,
      _endDt: endDt,
      _durationDays: 1,
    };
  } else {
    startDt = event.startDate
      ? DateTime.fromISO(event.startDate).toUTC().startOf("day")
      : DateTime.fromJSDate(event.start).toUTC().startOf("day");
    endDt = event.endDate
      ? DateTime.fromISO(event.endDate).toUTC().startOf("day")
      : DateTime.fromJSDate(event.end).toUTC().startOf("day");
  }
  
  return {
    ...event,
    _startDt: startDt,
    _endDt: endDt,
    _durationDays: Math.max(1, Math.ceil(endDt.diff(startDt, "days").days)),
  };
}

/**
 * Returns the duration of an event in calendar days.
 * 
 * @param {NormalizedEvent} event - Normalized event
 * @returns {number} Duration in days (minimum 1)
 */
function getEventDuration(event) {
  return event._durationDays || 1;
}

/**
 * Collects all weekday dates (Mon-Fri) within an event's date range.
 * Used to build the date-to-events overlap map.
 * 
 * @param {NormalizedEvent} event - Normalized event
 * @returns {string[]} ISO date strings (YYYY-MM-DD) for each weekday in range
 */
function getEventDates(event) {
  const dates = [];
  let current = event._startDt;
  const end = event._endDt;
  
  while (current <= end) {
    const isWeekday = current.weekday !== 6 && current.weekday !== 7;
    if (isWeekday) {
      dates.push(current.toISODate());
    }
    current = current.plus({ days: 1 });
  }
  
  return dates;
}

/**
 * Splits multi-day events at weekend boundaries into separate week segments.
 * 
 * This ensures each calendar week displays events independently, allowing
 * proper per-week grouping. Holidays and single-day events pass through unchanged.
 * 
 * Example: A 10-day vacation (Mon-Wed spanning 2 weeks) becomes 2 separate events:
 * - Week 1: Mon-Fri segment
 * - Week 2: Mon-Wed segment
 * 
 * @param {NormalizedEvent[]} events - Normalized events to potentially split
 * @returns {NormalizedEvent[]} Events with multi-week spans split into segments
 */
function splitEventsByWeekend(events) {
  const result = [];
  
  for (const event of events) {
    if (event.eventType === EVENT_TYPES.HOLIDAY || event._durationDays <= 1) {
      result.push(event);
      continue;
    }
    
    let current = event._startDt;
    const eventEnd = event._endDt;
    
    while (current <= eventEnd) {
      while ((current.weekday === 6 || current.weekday === 7) && current <= eventEnd) {
        current = current.plus({ days: 1 });
      }
      
      if (current > eventEnd) break;
      
      let segmentEnd = current;
      while (
        segmentEnd < eventEnd &&
        segmentEnd.weekday !== 5 &&
        segmentEnd.plus({ days: 1 }).weekday !== 6
      ) {
        const nextDay = segmentEnd.plus({ days: 1 });
        if (nextDay.weekday === 6 || nextDay.weekday === 7) break;
        segmentEnd = nextDay;
      }
      
      if (segmentEnd > eventEnd) {
        segmentEnd = eventEnd;
      }
      
      result.push({
        ...event,
        _startDt: current,
        _endDt: segmentEnd,
        _durationDays: Math.max(1, Math.ceil(segmentEnd.diff(current, "days").days) + 1),
        start: current.set({ hour: 12 }).toJSDate(),
        end: segmentEnd.set({ hour: 12 }).toJSDate(),
        _isSplit: true,
        _originalStartDt: event._startDt,
        _originalEndDt: event._endDt,
      });
      
      current = segmentEnd.plus({ days: 1 });
      while ((current.weekday === 6 || current.weekday === 7) && current <= eventEnd) {
        current = current.plus({ days: 1 });
      }
    }
  }
  
  return result;
}

/**
 * Creates a lookup map from dates to events occurring on each date.
 * Used to detect which events overlap on the same calendar day.
 * 
 * @param {NormalizedEvent[]} events - All normalized events
 * @returns {Map<string, NormalizedEvent[]>} Map of ISO date → events on that date
 */
function buildDateToEventsMap(events) {
  const dateMap = new Map();
  
  for (const event of events) {
    const dates = getEventDates(event);
    for (const dateStr of dates) {
      if (!dateMap.has(dateStr)) {
        dateMap.set(dateStr, []);
      }
      dateMap.get(dateStr).push(event);
    }
  }
  
  return dateMap;
}

/**
 * Partitions events into buckets by their status field.
 * 
 * @param {NormalizedEvent[]} events - Events to partition
 * @returns {Object.<string, NormalizedEvent[]>} Status string → events with that status
 */
function groupEventsByStatus(events) {
  return events.reduce((groupedByStatus, event) => {
    const status = event.status || "unknown";
    if (!groupedByStatus[status]) {
      groupedByStatus[status] = [];
    }
    groupedByStatus[status].push(event);
    return groupedByStatus;
  }, {});
}

/**
 * Creates a grouped event that represents multiple individual events.
 * The grouped event displays as "N integrante(s)" in the calendar.
 * 
 * @param {NormalizedEvent[]} events - Events to combine into a group
 * @param {import('luxon').DateTime} startDt - Earliest start date in the group
 * @param {import('luxon').DateTime} endDt - Latest end date in the group
 * @returns {NormalizedEvent} A grouped event container with `_isGrouped: true`
 */
function createGroupedEvent(events, startDt, endDt) {
  const customGroupId = events.map((e) => e.vacationId || e.id).join(",");
  
  return {
    id: customGroupId,
    range: `${startDt.toISO()} - ${endDt.toISO()}`,
    groupedEvents: events,
    start: startDt.set({ hour: 12 }).toJSDate(),
    end: endDt.set({ hour: 12 }).toJSDate(),
    startDate: startDt.set({ hour: 12 }).toJSDate(),
    endDate: endDt.set({ hour: 12 }).toJSDate(),
    title: `${events.length} integrante${events.length > 1 ? "s" : ""}`,
    status: events[0].status,
    eventType: EVENT_TYPES.VACATION,
    _isGrouped: true,
    _durationDays: Math.max(1, Math.ceil(endDt.diff(startDt, "days").days) + 1),
  };
}

/**
 * Generates a unique identifier for an event segment.
 * 
 * For events split across weekends, appends the segment's start date to ensure
 * each week is processed independently during grouping.
 * 
 * @param {NormalizedEvent} event - Event to identify
 * @returns {string} Unique segment ID (e.g., "42" or "42-2025-12-22")
 */
function getEventSegmentId(event) {
  if (event._isSplit && event._startDt) {
    return `${event.vacationId}-${event._startDt.toISODate()}`;
  }
  return String(event.vacationId);
}

/**
 * Sorts events for calendar display using a multi-level sort:
 * 1. Holidays always appear last (lowest visual priority)
 * 2. Longer duration events appear before shorter ones
 * 3. Higher-priority statuses (per priorityMap) appear before lower ones
 * 
 * @param {NormalizedEvent[]} events - Events to sort
 * @param {PriorityMap} priorityMap - Status-to-priority lookup
 * @returns {NormalizedEvent[]} New array of sorted events
 */
function sortByDurationAndPriority(events, priorityMap) {
  return [...events].sort((a, b) => {
    if (a.eventType === EVENT_TYPES.HOLIDAY && b.eventType !== EVENT_TYPES.HOLIDAY) return 1;
    if (b.eventType === EVENT_TYPES.HOLIDAY && a.eventType !== EVENT_TYPES.HOLIDAY) return -1;
    
    const durationA = getEventDuration(a);
    const durationB = getEventDuration(b);
    if (durationA !== durationB) return durationB - durationA;
    
    const priorityA = priorityMap[a.status] || 999;
    const priorityB = priorityMap[b.status] || 999;
    if (priorityA !== priorityB) return priorityA - priorityB;
    
    const titleA = (a.title || "").toLowerCase();
    const titleB = (b.title || "").toLowerCase();
    return titleA.localeCompare(titleB);
  });
}

/**
 * @typedef {Object} EventBounds
 * @property {import('luxon').DateTime} minStart - Earliest start date
 * @property {import('luxon').DateTime} maxEnd - Latest end date
 */

/**
 * Computes the bounding date range for a collection of events.
 * 
 * @param {NormalizedEvent[]} events - Events to compute bounds for
 * @returns {EventBounds} The earliest start and latest end dates
 */
function computeEventBounds(events) {
  let minStart = events[0]._startDt;
  let maxEnd = events[0]._endDt;
  
  for (const event of events) {
    if (event._startDt < minStart) minStart = event._startDt;
    if (event._endDt > maxEnd) maxEnd = event._endDt;
  }
  
  return { minStart, maxEnd };
}

/**
 * Expands existing bounds to include a new event's date range.
 * 
 * @param {EventBounds} bounds - Current bounds
 * @param {NormalizedEvent} event - Event to include in bounds
 * @returns {EventBounds} Expanded bounds
 */
function expandBoundsWithEvent(bounds, event) {
  return {
    minStart: event._startDt < bounds.minStart ? event._startDt : bounds.minStart,
    maxEnd: event._endDt > bounds.maxEnd ? event._endDt : bounds.maxEnd,
  };
}

/**
 * Determines if an event overlaps with a given date range.
 * 
 * @param {NormalizedEvent} event - Event to check
 * @param {EventBounds} bounds - Date range to check against
 * @returns {boolean} True if the event overlaps with the bounds
 */
function doEventsOverlap(event, bounds) {
  return event._startDt <= bounds.maxEnd && event._endDt >= bounds.minStart;
}

/**
 * Finds all events that transitively overlap with a seed set and absorbs 
 * already-processed events from finalEvents.
 * 
 * This implements a transitive closure: if event A overlaps with event B,
 * and event B overlaps with event C, then all three are grouped together.
 * The bounds expand as each overlapping event is absorbed, potentially
 * catching additional events that overlap with the expanded range.
 * 
 * @param {Object} params - Cluster detection parameters
 * @param {NormalizedEvent[]} params.seedEvents - Initial events to start clustering from
 * @param {NormalizedEvent[]} params.candidateEvents - Pool of events to search for overlaps
 * @param {NormalizedEvent[]} params.finalEvents - Already-processed events that may be absorbed
 * @param {Set<string>} params.processedEventIds - IDs of already-processed events
 * @param {string} params.targetStatus - Only absorb events with this status
 * @returns {{ absorbed: NormalizedEvent[], bounds: EventBounds, indicesToRemove: number[] }}
 */
function findTransitiveOverlapCluster({
  seedEvents,
  candidateEvents,
  finalEvents,
  processedEventIds,
  targetStatus,
}) {
  const absorbed = [];
  let bounds = computeEventBounds(seedEvents);
  
  for (const event of candidateEvents) {
    const segmentId = getEventSegmentId(event);
    const isUnprocessed = !processedEventIds.has(segmentId);
    const matchesStatus = event.status === targetStatus;
    
    if (isUnprocessed && matchesStatus && doEventsOverlap(event, bounds)) {
      absorbed.push(event);
      bounds = expandBoundsWithEvent(bounds, event);
    }
  }
  
  const indicesToRemove = [];
  
  for (let i = 0; i < finalEvents.length; i++) {
    const existingEvent = finalEvents[i];
    const isGroupedOrHoliday = existingEvent._isGrouped || existingEvent.eventType === EVENT_TYPES.HOLIDAY;
    const matchesStatus = existingEvent.status === targetStatus;
    
    if (!isGroupedOrHoliday && matchesStatus && doEventsOverlap(existingEvent, bounds)) {
      absorbed.push(existingEvent);
      indicesToRemove.push(i);
      bounds = expandBoundsWithEvent(bounds, existingEvent);
    }
  }
  
  return { absorbed, bounds, indicesToRemove };
}


/**
 * @typedef {Object} SlotAllocation
 * @property {NormalizedEvent[]} individuals - Events to display individually
 * @property {Record<string, NormalizedEvent[]>} groupedByStatus - Events grouped by their status
 */

/**
 * Determines the allocation of events for a specific date into individual slots and groups.
 * 
 * Allocation rules:
 * - Statuses with only 1 event always display individually
 * - Statuses with 2+ events may form groups if slots are limited
 * - Remaining individual slots are distributed evenly among groupable statuses
 * - Single-member groups are promoted to individuals as a safety net
 * 
 * @param {NormalizedEvent[]} eventsOnDate - All events on this date
 * @param {number} maxSlots - Maximum slots available (typically 4)
 * @param {PriorityMap} priorityMap - Status-to-priority lookup
 * @returns {SlotAllocation} Allocation of events into individuals and groups
 */
function allocateSlotsForDate(eventsOnDate, maxSlots, priorityMap) {
  const sorted = sortByDurationAndPriority(eventsOnDate, priorityMap);
  
  const holidays = sorted.filter(event => event.eventType === EVENT_TYPES.HOLIDAY);
  const vacations = sorted.filter(event => event.eventType !== EVENT_TYPES.HOLIDAY);
  
  if (eventsOnDate.length <= maxSlots) {
    const sortedWithHolidaysLast = [...vacations, ...holidays];
    return { individuals: sortedWithHolidaysLast, groupedByStatus: {} };
  }
  
  const vacationsByStatus = {};
  for (const vacation of vacations) {
    if (!vacationsByStatus[vacation.status]) vacationsByStatus[vacation.status] = [];
    vacationsByStatus[vacation.status].push(vacation);
  }
  
  const groupableStatuses = Object.entries(vacationsByStatus)
    .filter(([, statusEvents]) => statusEvents.length >= 2)
    .map(([status]) => status);
  
  const singleEventStatuses = Object.entries(vacationsByStatus)
    .filter(([, statusEvents]) => statusEvents.length === 1)
    .map(([status]) => status);
  
  const slotsForHolidays = holidays.length;
  const slotsForSingles = singleEventStatuses.length;
  const slotsForGroups = groupableStatuses.length;
  const slotsForIndividualsFromGroups = Math.max(0, maxSlots - slotsForHolidays - slotsForSingles - slotsForGroups);
  
  const individuals = [];
  const groupedByStatus = {};
  
  for (const status of singleEventStatuses) {
    individuals.push(vacationsByStatus[status][0]);
  }
  
  let individualSlotsUsed = 0;
  const individualSlotsPerGroupable = slotsForIndividualsFromGroups > 0 && groupableStatuses.length > 0
    ? Math.floor(slotsForIndividualsFromGroups / groupableStatuses.length)
    : 0;
  
  for (const status of groupableStatuses) {
    const statusEvents = vacationsByStatus[status];
    let individualsFromStatus = 0;
    
    for (const vacation of statusEvents) {
      if (individualsFromStatus < individualSlotsPerGroupable && individualSlotsUsed < slotsForIndividualsFromGroups) {
        individuals.push(vacation);
        individualSlotsUsed++;
        individualsFromStatus++;
      } else {
        if (!groupedByStatus[status]) groupedByStatus[status] = [];
        groupedByStatus[status].push(vacation);
      }
    }
  }
  
  individuals.push(...holidays);
  
  for (const status of Object.keys(groupedByStatus)) {
    if (groupedByStatus[status].length === 0) {
      delete groupedByStatus[status];
    } else if (groupedByStatus[status].length === 1) {
      individuals.push(groupedByStatus[status][0]);
      delete groupedByStatus[status];
    }
  }
  
  return { individuals, groupedByStatus };
}

/**
 * @typedef {Object} DateAllocation
 * @property {Set<string>} individuals - Event IDs to display individually on this date
 * @property {Map<string, Set<string>>} grouped - Status → Set of event IDs to group on this date
 */

/**
 * Computes per-date allocation for all events, determining which are individual vs grouped on each date.
 * 
 * @param {NormalizedEvent[]} splitVacations - Weekend-split vacation events
 * @param {NormalizedEvent[]} holidays - Holiday events
 * @param {Map<string, NormalizedEvent[]>} dateMap - Date to events mapping
 * @param {PriorityMap} priorityMap - Status-to-priority lookup
 * @returns {Map<string, DateAllocation>} Map of ISO date string → allocation for that date
 */
function computeDateAllocations(splitVacations, holidays, dateMap, priorityMap) {
  const allocations = new Map();
  const dateEntries = Array.from(dateMap.entries()).sort();
  
  for (const [dateStr, eventsOnDate] of dateEntries) {
    const { individuals, groupedByStatus } = allocateSlotsForDate(
      eventsOnDate,
      MAX_EVENTS_PER_CELL,
      priorityMap
    );
    
    const individualIds = new Set(individuals.map(event => getEventSegmentId(event)));
    const groupedMap = new Map();
    
    for (const [status, statusEvents] of Object.entries(groupedByStatus)) {
      groupedMap.set(status, new Set(statusEvents.map(event => getEventSegmentId(event))));
    }
    
    allocations.set(dateStr, { individuals: individualIds, grouped: groupedMap });
  }
  
  return allocations;
}

/**
 * @typedef {Object} DateRange
 * @property {import('luxon').DateTime} start - Range start date
 * @property {import('luxon').DateTime} end - Range end date
 */

/**
 * @typedef {Object} EventSegmentResult
 * @property {DateRange[]} individualRanges - Date ranges where event displays individually
 * @property {string[]} groupedDates - ISO dates where event is part of a group
 */

/**
 * Computes event segments based on per-date allocations.
 * An event may be split into multiple segments: some individual, some grouped.
 * 
 * @param {NormalizedEvent} event - The event to compute segments for
 * @param {Map<string, DateAllocation>} dateAllocations - Per-date allocation map
 * @returns {EventSegmentResult} Ranges for individual display and dates for grouped display
 */
function computeEventSegments(event, dateAllocations) {
  const eventId = getEventSegmentId(event);
  const eventDates = getEventDates(event);
  
  const individualRanges = [];
  const groupedDates = [];
  
  let currentIndividualStart = null;
  let currentIndividualEnd = null;
  
  for (const dateStr of eventDates) {
    const allocation = dateAllocations.get(dateStr);
    if (!allocation) continue;
    
    const isIndividual = allocation.individuals.has(eventId);
    
    if (isIndividual) {
      const date = DateTime.fromISO(dateStr);
      if (currentIndividualStart === null) {
        currentIndividualStart = date;
        currentIndividualEnd = date;
      } else {
        if (date.diff(currentIndividualEnd, 'days').days <= 3) {
          currentIndividualEnd = date;
        } else {
          individualRanges.push({ start: currentIndividualStart, end: currentIndividualEnd });
          currentIndividualStart = date;
          currentIndividualEnd = date;
        }
      }
    } else {
      if (currentIndividualStart !== null) {
        individualRanges.push({ start: currentIndividualStart, end: currentIndividualEnd });
        currentIndividualStart = null;
        currentIndividualEnd = null;
      }
      groupedDates.push(dateStr);
    }
  }
  
  if (currentIndividualStart !== null) {
    individualRanges.push({ start: currentIndividualStart, end: currentIndividualEnd });
  }
  
  return { individualRanges, groupedDates };
}

/**
 * Creates individual event segments from computed ranges.
 * 
 * @param {NormalizedEvent} originalEvent - The original event
 * @param {Array<{start: DateTime, end: DateTime}>} ranges - Date ranges for individual segments
 * @returns {NormalizedEvent[]} Individual event segments
 */
function createIndividualSegments(originalEvent, ranges) {
  if (ranges.length === 0) return [];
  
  if (ranges.length === 1 &&
      ranges[0].start.toISODate() === originalEvent._startDt.toISODate() &&
      ranges[0].end.toISODate() === originalEvent._endDt.toISODate()) {
    return [originalEvent];
  }
  
  return ranges.map((range, index) => ({
    ...originalEvent,
    _startDt: range.start,
    _endDt: range.end,
    _durationDays: Math.max(1, Math.ceil(range.end.diff(range.start, 'days').days) + 1),
    start: range.start.set({ hour: 12 }).toJSDate(),
    end: range.end.set({ hour: 12 }).toJSDate(),
    _isSplit: true,
    _segmentIndex: index,
    _originalStartDt: originalEvent._startDt,
    _originalEndDt: originalEvent._endDt,
  }));
}

/**
 * @typedef {Object} DailyGroupEntry
 * @property {string} date - ISO date string (YYYY-MM-DD)
 * @property {string} status - Vacation status
 * @property {Set<string>} eventIds - Set of vacation IDs in this group
 * @property {NormalizedEvent[]} events - Events in this group
 */

/**
 * @typedef {Object} MergedGroupEntry
 * @property {string} status - Vacation status
 * @property {string} idsKey - Comma-joined sorted event IDs (for identity comparison)
 * @property {string} startDate - ISO start date
 * @property {string} endDate - ISO end date
 * @property {NormalizedEvent[]} events - Events in this group
 */

/**
 * Merges consecutive daily groups of the same events into multi-day groups.
 * 
 * Groups are merged when they have the same status and same event members,
 * and are within 3 days of each other (to span weekends).
 * 
 * @param {DailyGroupEntry[]} dailyGroups - Daily group entries to merge
 * @returns {NormalizedEvent[]} Merged grouped events ready for calendar display
 */
function mergeConsecutiveGroups(dailyGroups) {
  if (dailyGroups.length === 0) return [];
  
  dailyGroups.sort((a, b) => {
    const statusCompare = a.status.localeCompare(b.status);
    if (statusCompare !== 0) return statusCompare;
    return a.date.localeCompare(b.date);
  });
  
  const mergedGroups = [];
  let currentGroup = null;
  
  for (const dailyGroup of dailyGroups) {
    const groupEventIdsKey = [...dailyGroup.eventIds].sort().join(',');
    
    if (currentGroup === null) {
      currentGroup = {
        status: dailyGroup.status,
        idsKey: groupEventIdsKey,
        startDate: dailyGroup.date,
        endDate: dailyGroup.date,
        events: dailyGroup.events,
      };
    } else if (currentGroup.status === dailyGroup.status && currentGroup.idsKey === groupEventIdsKey) {
      const prevDate = DateTime.fromISO(currentGroup.endDate);
      const currDate = DateTime.fromISO(dailyGroup.date);
      const daysDiff = currDate.diff(prevDate, 'days').days;
      
      if (daysDiff <= 3) {
        currentGroup.endDate = dailyGroup.date;
      } else {
        mergedGroups.push(currentGroup);
        currentGroup = {
          status: dailyGroup.status,
          idsKey: groupEventIdsKey,
          startDate: dailyGroup.date,
          endDate: dailyGroup.date,
          events: dailyGroup.events,
        };
      }
    } else {
      mergedGroups.push(currentGroup);
      currentGroup = {
        status: dailyGroup.status,
        idsKey: groupEventIdsKey,
        startDate: dailyGroup.date,
        endDate: dailyGroup.date,
        events: dailyGroup.events,
      };
    }
  }
  
  if (currentGroup !== null) {
    mergedGroups.push(currentGroup);
  }
  
  return mergedGroups.map(group => {
    const startDt = DateTime.fromISO(group.startDate);
    const endDt = DateTime.fromISO(group.endDate);
    return createGroupedEvent(group.events, startDt, endDt);
  });
}

/**
 * Separates raw events into holidays and deduplicated vacations, normalizing dates.
 * 
 * @param {RawVacationEvent[]} rawEvents - Raw events from API
 * @returns {{ holidays: NormalizedEvent[], vacations: NormalizedEvent[] }}
 */
function prepareEvents(rawEvents) {
  const holidays = [];
  const vacationsMap = new Map();
  
  for (const event of rawEvents) {
    if (event.eventType === EVENT_TYPES.HOLIDAY) {
      holidays.push(normalizeEventDates(event));
    } else if (event.eventType === EVENT_TYPES.VACATION) {
      if (!vacationsMap.has(event.vacationId)) {
        vacationsMap.set(event.vacationId, normalizeEventDates(event));
      }
    }
  }
  
  const vacations = Array.from(vacationsMap.values());
  vacations.sort((a, b) => a._startDt.toMillis() - b._startDt.toMillis());
  
  return { holidays, vacations };
}

/**
 * Processes raw vacation and holiday events for calendar display.
 * 
 * ## Algorithm Phases
 * 
 * 1. **Prepare** - Separate holidays/vacations, deduplicate, normalize dates
 * 2. **Split by Weekend** - Break multi-week events into per-week segments
 * 3. **Compute Allocations** - Determine per-date slot allocation (individuals vs groups)
 * 4. **Process Events** - Create individual segments and collect grouped dates
 * 5. **Merge Groups** - Combine consecutive same-member groups into multi-day groups
 * 6. **Finalize** - Sort by duration and priority, assign sequential IDs
 * 
 * ## Key Behaviors
 * 
 * - **Slot allocation**: Priority order is Duration (desc) → Status → Alphabetical
 * - **Partial grouping**: Only excess events (>4 per cell) are grouped, not all same-status
 * - **Event breakup**: Events can be split at day boundaries when they fit individually
 *   on some days but must be grouped on others
 * - **Multi-day groups**: Consecutive days with same grouped events form single group bars
 * - **Holidays**: Always shown individually, count toward 4-slot limit, never grouped
 * 
 * @param {RawVacationEvent[]} rawEvents - Raw vacation/holiday events from API
 * @returns {NormalizedEvent[]} Processed events ready for React Big Calendar
 */
function customGroupEventsByOverlap(rawEvents) {
  if (!rawEvents || rawEvents.length === 0) return [];
  
  const { holidays, vacations } = prepareEvents(rawEvents);
  
  const hasApprovedEvents = vacations.some(
    e => e.status === VACATION_STATES.APPROVED || 
         e.status === VACATION_STATES.APPROVED_AND_PENDING_PLUS_DAYS_PAY_DATE
  );
  const priorityMap = hasApprovedEvents ? UPCOMING_PRIORITY : PENDING_PRIORITY;
  
  const splitVacations = splitEventsByWeekend(vacations);
  const allEvents = [...holidays, ...splitVacations];
  const dateMap = buildDateToEventsMap(allEvents);
  const dateAllocations = computeDateAllocations(splitVacations, holidays, dateMap, priorityMap);
  
  const finalEvents = [];
  const dailyGroups = [];
  const processedVacationIds = new Set();
  
  for (const holiday of holidays) {
    finalEvents.push(holiday);
  }
  
  for (const vacation of splitVacations) {
    const vacationId = getEventSegmentId(vacation);
    if (processedVacationIds.has(vacationId)) continue;
    processedVacationIds.add(vacationId);
    
    const { individualRanges, groupedDates } = computeEventSegments(vacation, dateAllocations);
    
    const individualSegments = createIndividualSegments(vacation, individualRanges);
    for (const segment of individualSegments) {
      finalEvents.push(segment);
    }
    
    for (const dateStr of groupedDates) {
      const allocation = dateAllocations.get(dateStr);
      if (!allocation) continue;
      
      for (const [status, eventIds] of allocation.grouped) {
        if (eventIds.has(vacationId)) {
          let dailyGroupEntry = dailyGroups.find(g => g.date === dateStr && g.status === status);
          if (!dailyGroupEntry) {
            dailyGroupEntry = { date: dateStr, status, eventIds: new Set(), events: [] };
            dailyGroups.push(dailyGroupEntry);
          }
          dailyGroupEntry.eventIds.add(String(vacation.vacationId));
          dailyGroupEntry.events.push(vacation);
          break;
        }
      }
    }
  }
  
  const mergedGroupedEvents = mergeConsecutiveGroups(dailyGroups);
  for (const groupedEvent of mergedGroupedEvents) {
    finalEvents.push(groupedEvent);
  }
  
  const result = sortByDurationAndPriority(finalEvents, priorityMap).map((event, index) => ({
    ...event,
    id: index,
  }));

  return result;
}
/** @param {VacationRequest[]} events */
export const defaultGroupEventsByOverlap = (events) => {
  return customGroupEventsByOverlap(events)
}

export const defaultBigCalendarMonthHeader = ({ date }) => {
  const luxonDate = DateTime.fromJSDate(date)
  const day = luxonDate.toFormat("dd")
  const isWeekend = luxonDate.weekday === 6 || luxonDate.weekday === 7
  return <div className={`${isWeekend && "rbc-weekend"}`}>{day}</div>
}



export const formatVacationEventTemplate = (
  /** @type {VacationRequest} */
  event,
  theme
) => {
  const icons = {
    [VACATION_STATES.APPROVED_AND_PENDING_PLUS_DAYS_PAY_DATE]: (
      <PaidIcon className="icon" />
    ),
    [VACATION_STATES.APPROVED]: <ThumbUpIcon className="icon" />,
    [VACATION_STATES.CHANGE_REQUEST]: <ChangeCircle className="icon" />,
    [VACATION_STATES.CANCEL_REQUEST]: <BlockedIcon className="icon" />,
    [VACATION_STATES.PENDING]: <ClockIcon className="icon" />,
    [VACATION_STATES.REJECTED]: <ThumbDownIcon className="icon" />,
    [VACATION_STATES.CANCELED]: <BlockedIcon className="icon" />,
    [EVENT_TYPES.PERIOD_START]: <i className="pi pi-bookmark-fill text-xs" />,
    [EVENT_TYPES.PERIOD_END]: <i className="pi pi-bookmark-fill" />,
  }

  const icon = icons[event.status ?? event.eventType]

  let title
  if (!icon && event.groupedEvents?.length > 0) {
    title = (
      <div className="flex gap-2 align-items-center">
        <BeachAccessOutlinedIcon fontSize="small" />
        <span className="event-status--grouped">
          {event.groupedEvents?.length} integrantes
        </span>
      </div>
    )
  } else {
    title = (
      <div className="flex gap-2 align-items-center pointer-events-none">
        {icon}
        <span className={clsx(`event-status--${event.status}`)}>
          {event.title}
        </span>
      </div>
    )
  }

  if (
    event.eventType === EVENT_TYPES.HOLIDAY &&
    event.holidaySchema === HOLIDAY_SCHEMAS.MEXICAN
  ) {
    title = (
      <div className="flex gap-2 align-items-center">
        <img src={MexicanFlagIcon} alt="Mexico Flag" />
        <span className="event-status--holiday">{event.title}</span>
      </div>
    )
  }

  if (event.eventType === EVENT_TYPES.GLOBAL_SISU) {
    title = (
      <div className="flex gap-2 align-items-center">
        <img src={SISUIcon} alt="SISU Icon" />
        <span className="event-status--sisu">{event.title}</span>
      </div>
    )
  }

  if (isEventPeriodDelimiter(event)) {
    title = (
      <div className="flex gap-2 align-items-center">
        {icon}
        <span className={`event-status--${event.eventType}`}>
          {event.title}
        </span>
      </div>
    )
  }
  return {
    ...event,
    prevTitle: event.title,
    title,
  }
}


export const formatVacationEventsTemplates = (
  /** @type {VacationRequest[]} */
  events,
  theme
) => {
  const formattedEvents = events.map((event) => formatVacationEventTemplate(event, theme))
  return formattedEvents
}
